var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "nativewind/jsx-runtime";
import ImageEditor from '@react-native-community/image-editor';
import { useAtom, useAtomValue } from 'jotai';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ActivityIndicator, Animated, Platform, StyleSheet, } from 'react-native';
import DraggableFlatList from 'react-native-draggable-flatlist';
import { launchImageLibrary, } from 'react-native-image-picker';
import Svg, { Line } from 'react-native-svg';
import { CameraCaptureError } from 'react-native-vision-camera';
import CloseIcon from '@/assets/icon/close.svg';
import AlbumIcon from '@/assets/icon/ic_album.svg';
import GuideIcon from '@/assets/icon/ic_camera_guide.svg';
import StairsOverlayIcon from '@/assets/icon/ic_camera_overlay_stairs.svg';
import CircleCloseIcon from '@/assets/icon/ic_circle_close.svg';
import CircleInfoIcon from '@/assets/icon/ic_circle_info.svg';
import ClockIcon from '@/assets/icon/ic_clock.svg';
import FlashOffIcon from '@/assets/icon/ic_flash_off.svg';
import FlashOnIcon from '@/assets/icon/ic_flash_on.svg';
import { featureFlagAtom } from '@/atoms/Auth';
import { hasShownAlbumLockedTooltipAtom, hasShownCameraGuideTooltipAtom, hasShownGuideForEntrancePhotoAtom, hasShownGuideForReviewPhotoAtom, hasShownGuideForToiletPhotoAtom, isCameraGuideOverlayEnabledAtom, lastKnownAlbumUploadAllowedAtom, } from '@/atoms/User';
import { ScreenLayout } from '@/components/ScreenLayout';
import Tooltip from '@/components/Tooltip';
import { color } from '@/constant/color';
import { MAX_NUMBER_OF_TAKEN_PHOTOS } from '@/constant/constant';
import Logger from '@/logging/Logger';
import { SccCameraButtons } from '@/native-modules/SccCameraButtons';
import EntrancePhotoGuideCarousel from '@/screens/PlacePhotoGuideScreen/EntrancePhotoGuideCarousel';
import HeatTelemetry from '@/utils/HeatTelemetry';
import ImageFileUtils from '@/utils/ImageFileUtils';
import ToastUtils from '@/utils/ToastUtils';
import { useBackHandler } from '@react-native-community/hooks';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import CameraDeviceSelect from './CameraDeviceSelect';
import CameraNotAuthorized from './CameraNotAuthorized';
import CameraPreview from './CameraPreview';
import * as S from './CameraScreen.style';
import useCamera from './useCamera';
/** 입구(장소/건물) 촬영인지 — 가이드 오버레이·예시 사진·툴팁 노출 분기의 기준. */
export function isEntranceTarget(target) {
    return target === 'placeEntrance' || target === 'buildingEntrance';
}
const ENTRANCE_PHOTO_SLOT_PLACEHOLDERS = [
    require('@/assets/img/photo_slot_door.png'),
    require('@/assets/img/photo_slot_stairs.png'),
    null, // 3번째 슬롯은 안내 일러스트 없이 빈 칸
];
export default function CameraScreen({ route, navigation, }) {
    var _a, _b;
    const initialFocusedIndex = React.useRef(0);
    const { camera, hasPermission, device, setDevice } = useCamera();
    const [photoFiles, setPhotoFiles] = useState([]);
    const [flash, setFlash] = useState('off');
    const flashRef = useRef(flash);
    flashRef.current = flash;
    const [timerSeconds, setTimerSeconds] = useState(0);
    const [countdownDisplay, setCountdownDisplay] = useState(null);
    const countdownTimeoutRef = useRef(null);
    const [hasShownGuideForEnterancePhoto, setHasShownGuideForEnterancePhoto] = useAtom(hasShownGuideForEntrancePhotoAtom);
    const [isEntranceGuideVisible, setIsEntranceGuideVisible] = useState(false);
    const hasShownGuideForReviewPhoto = useAtomValue(hasShownGuideForReviewPhotoAtom);
    const hasShownGuideForToiletPhoto = useAtomValue(hasShownGuideForToiletPhotoAtom);
    const featureFlag = useAtomValue(featureFlagAtom);
    const isAlbumUploadAllowed = (_a = featureFlag === null || featureFlag === void 0 ? void 0 : featureFlag.isAlbumUploadAllowed) !== null && _a !== void 0 ? _a : false;
    const [isTakingPhoto, setIsTakingPhoto] = useState(false);
    const [isLoadingAlbum, setIsLoadingAlbum] = useState(false);
    const [isGuideOverlayEnabled, setIsGuideOverlayEnabled] = useAtom(isCameraGuideOverlayEnabledAtom);
    const [lastKnownAlbumUploadAllowed, setLastKnownAlbumUploadAllowed] = useAtom(lastKnownAlbumUploadAllowedAtom);
    const [hasShownAlbumLockedTooltip, setHasShownAlbumLockedTooltip] = useAtom(hasShownAlbumLockedTooltipAtom);
    const [hasShownCameraGuideTooltip, setHasShownCameraGuideTooltip] = useAtom(hasShownCameraGuideTooltipAtom);
    const [visibleTooltip, setVisibleTooltip] = useState(null);
    const isEntrance = isEntranceTarget(route.params.target);
    // 기존 촬영한 이미지 체크
    useEffect(() => {
        if (route.params && route.params.takenPhotos) {
            setPhotoFiles(route.params.takenPhotos);
        }
    }, [route.params]);
    useEffect(() => {
        if (isEntrance && !hasShownGuideForEnterancePhoto) {
            setIsEntranceGuideVisible(true);
            setHasShownGuideForEnterancePhoto(true);
        }
        else if (route.params.target === 'review' &&
            !hasShownGuideForReviewPhoto) {
            openGuide('review');
        }
        else if (route.params.target === 'toilet' &&
            !hasShownGuideForToiletPhoto) {
            openGuide('toilet');
        }
    }, [
        isEntrance,
        route.params.target,
        hasShownGuideForEnterancePhoto,
        hasShownGuideForReviewPhoto,
        hasShownGuideForToiletPhoto,
    ]);
    // 안드로이드 백 버튼: 예시 사진 오버레이가 열려 있으면 카메라를 나가지 않고 오버레이만 닫는다.
    useBackHandler(() => {
        if (isEntranceGuideVisible) {
            setIsEntranceGuideVisible(false);
            return true;
        }
        return false;
    });
    // 앨범 활성화 안내 툴팁(1-5): 입구 촬영 최초 진입 시 1건만 판정한다.
    // 예시 사진 캐러셀이 떠 있는 동안은 미룬다 — 캐러셀에 가려진 채 5초 타이머가
    // 만료되어 툴팁을 아예 못 보는 문제가 있었다.
    const hasJudgedTooltipRef = useRef(false);
    useEffect(() => {
        // 캐러셀이 "곧 열릴 예정"인 경우까지 포함해야 한다. 같은 mount 의 effect flush 에서
        // 캐러셀 effect 가 setIsEntranceGuideVisible(true) 를 호출해도, 바로 뒤 실행되는 이
        // effect 는 그 렌더의 클로저(false)를 읽기 때문에 isEntranceGuideVisible 만으로는
        // 최초 진입을 못 막는다 — 캐러셀에 가린 채 5초 타이머가 소진되던 원인.
        const willOpenEntranceGuide = isEntrance && !hasShownGuideForEnterancePhoto;
        if (!isEntrance ||
            isEntranceGuideVisible ||
            willOpenEntranceGuide ||
            hasJudgedTooltipRef.current) {
            return;
        }
        hasJudgedTooltipRef.current = true;
        const justActivated = lastKnownAlbumUploadAllowed === false && isAlbumUploadAllowed === true;
        if (!isAlbumUploadAllowed) {
            if (!hasShownAlbumLockedTooltip) {
                setVisibleTooltip('albumLocked');
                setHasShownAlbumLockedTooltip(true);
            }
        }
        else if (justActivated) {
            setVisibleTooltip('albumActivated');
        }
        else if (!hasShownCameraGuideTooltip) {
            setVisibleTooltip('guideIntro');
            setHasShownCameraGuideTooltip(true);
        }
        setLastKnownAlbumUploadAllowed(isAlbumUploadAllowed);
    }, [isEntrance, isEntranceGuideVisible, hasShownGuideForEnterancePhoto]);
    const tooltipOpacity = useRef(new Animated.Value(0)).current;
    useEffect(() => {
        if (visibleTooltip === null) {
            return;
        }
        tooltipOpacity.setValue(1);
        const timeout = setTimeout(() => {
            Animated.timing(tooltipOpacity, {
                toValue: 0,
                duration: 300,
                useNativeDriver: true,
            }).start(({ finished }) => {
                if (finished) {
                    setVisibleTooltip(null);
                }
            });
        }, 5000);
        return () => clearTimeout(timeout);
    }, [visibleTooltip, tooltipOpacity]);
    function openGuide(target) {
        navigation.push('PlacePhotoGuide', { target: target });
    }
    function goBack() {
        navigation.goBack();
    }
    function confirm(_photoFiles) {
        if (isTakingPhoto) {
            return;
        }
        // TODO: navigation 에 non-serializable 값을 넘겨주면 안된다. (https://reactnavigation.org/docs/troubleshooting/#i-get-the-warning-non-serializable-values-were-found-in-the-navigation-state)
        if (route.params && route.params.onPhotosTaken) {
            route.params.onPhotosTaken(_photoFiles);
        }
        navigation.goBack();
    }
    function onPressX(target) {
        setPhotoFiles(photos => photos.filter(p => p !== target));
    }
    function toggleFlash() {
        setFlash(f => (f === 'on' ? 'off' : 'on'));
    }
    function cycleTimer() {
        setTimerSeconds(s => {
            switch (s) {
                case 0:
                    return 3;
                case 3:
                    return 5;
                case 5:
                    return 10;
                case 10:
                    return 0;
            }
        });
    }
    function cancelCountdown() {
        if (countdownTimeoutRef.current) {
            clearTimeout(countdownTimeoutRef.current);
            countdownTimeoutRef.current = null;
        }
        setCountdownDisplay(null);
    }
    function openPreview(index) {
        initialFocusedIndex.current = index;
        navigation.navigate('ImageZoomViewer', {
            imageUrls: photoFiles.map(file => ImageFileUtils.filepathFromImageFile(file)),
            index: index,
        });
    }
    // 사진 촬영에는 약간의 딜레이가 있으나, 로딩 레이어를 띄우지는 않는다.
    function takePhoto() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            try {
                setIsTakingPhoto(true);
                if (camera.current == null) {
                    throw new Error('Camera ref is null!');
                }
                const taken = yield camera.current.takePhoto({
                    flash: flashRef.current,
                });
                if (taken) {
                    const { cropped, size } = yield cropToRect(taken);
                    setPhotoFiles(photos => photos.concat({ uri: cropped.uri, width: size, height: size }));
                }
            }
            catch (error) {
                Logger.logError(error);
                if (error instanceof CameraCaptureError) {
                    ToastUtils.show('사진 촬영을 실패했습니다. ' + ((_a = error.cause) === null || _a === void 0 ? void 0 : _a.message));
                }
                else {
                    ToastUtils.show('사진 촬영을 실패했습니다. ' + error.message);
                }
            }
            finally {
                setIsTakingPhoto(false);
            }
        });
    }
    const photoLimit = (_b = route.params.maxPhotos) !== null && _b !== void 0 ? _b : MAX_NUMBER_OF_TAKEN_PHOTOS;
    const canTakeMore = photoFiles.length < photoLimit;
    function startCountdown(remaining) {
        if (remaining <= 0) {
            setCountdownDisplay(null);
            countdownTimeoutRef.current = null;
            takePhoto();
            return;
        }
        setCountdownDisplay(remaining);
        countdownTimeoutRef.current = setTimeout(() => {
            startCountdown(remaining - 1);
        }, 1000);
    }
    const handleCapturePress = useCallback(() => {
        if (!canTakeMore || isTakingPhoto) {
            return;
        }
        if (countdownDisplay !== null) {
            cancelCountdown();
            return;
        }
        if (timerSeconds === 0) {
            takePhoto();
        }
        else {
            startCountdown(timerSeconds);
        }
    }, [canTakeMore, isTakingPhoto, countdownDisplay, timerSeconds]);
    const handleCapturePressRef = useRef(handleCapturePress);
    handleCapturePressRef.current = handleCapturePress;
    useEffect(() => {
        return () => {
            if (countdownTimeoutRef.current) {
                clearTimeout(countdownTimeoutRef.current);
                countdownTimeoutRef.current = null;
            }
        };
    }, []);
    useEffect(() => {
        HeatTelemetry.start('camera_active');
        return () => HeatTelemetry.stop('camera_active');
    }, []);
    useEffect(() => {
        let lastTriggerTime = 0;
        let isMounted = true;
        SccCameraButtons.attach().catch(() => {
            // ignore — fallback path may not be available
        });
        const subscription = SccCameraButtons.addCapturePressListener(() => {
            if (!isMounted) {
                return;
            }
            // 빠른 연타 방어용 짧은 쿨다운. 네이티브 단에서 이미 정상화된
            // discrete press 이벤트지만 안전 마진으로 둔다.
            const now = Date.now();
            if (now - lastTriggerTime < 300) {
                return;
            }
            lastTriggerTime = now;
            handleCapturePressRef.current();
        });
        return () => {
            isMounted = false;
            subscription.remove();
            SccCameraButtons.detach().catch(() => { });
        };
    }, []);
    function selectFromAlbum() {
        return __awaiter(this, void 0, void 0, function* () {
            const options = {
                mediaType: 'photo',
                includeBase64: false,
                maxHeight: 2000,
                maxWidth: 2000,
                selectionLimit: photoLimit,
                // iOS에서 HEIC/HEIF를 JPEG로 변환해서 내려받는다.
                // iOS 26에서 HEIC 일부가 후속 압축/업로드 단계에서 실패하는 사례 회피.
                assetRepresentationMode: 'compatible',
            };
            const loadingTimer = setTimeout(() => setIsLoadingAlbum(true), 1000);
            try {
                launchImageLibrary(options, (response) => {
                    clearTimeout(loadingTimer);
                    setIsLoadingAlbum(false);
                    if (response.didCancel || response.errorMessage) {
                        const errorMessage = `didCancel ${response.didCancel} / errorCode: ${response.errorCode} / errorMessage: ${response.errorMessage}`;
                        Logger.logError(Error(errorMessage));
                        return;
                    }
                    if (response.assets) {
                        const newImages = response.assets.map(asset => ({
                            uri: asset.uri || '',
                            width: asset.width || 0,
                            height: asset.height || 0,
                        }));
                        confirm(newImages);
                    }
                });
            }
            catch (error) {
                clearTimeout(loadingTimer);
                setIsLoadingAlbum(false);
                Logger.logError(error);
            }
        });
    }
    return (_jsxs(ScreenLayout, { isHeaderVisible: true, safeAreaEdges: ['top', 'bottom'], style: {
            backgroundColor: color.gray70v2,
            // Figma(min 694 / max 844): 헤더~버튼행 블록이 safe area 안에서 세로 중앙.
            justifyContent: 'center',
        }, children: [_jsxs(S.Header, { children: [_jsx(S.HeaderCloseButton, { elementName: "camera_close_button", accessibilityRole: "button", accessibilityLabel: "\uB2EB\uAE30", onPress: goBack, children: _jsx(CloseIcon, { width: 16, height: 16, color: "white" }) }), _jsx(S.SubmitButton, { onPress: () => confirm(photoFiles), disabled: photoFiles.length === 0, children: `사진 등록(${photoFiles.length}/${photoLimit})` })] }), _jsxs(S.CameraContainer, { children: [hasPermission && device ? (_jsxs(S.CameraPreviewContainer, { children: [_jsx(CameraPreview, { ref: camera, device: device }), _jsx(CameraDeviceSelect, { device: device, onDeviceSelect: setDevice }), isEntrance && isGuideOverlayEnabled && photoFiles.length === 0 && (_jsxs(S.DoorFrameOverlay, { pointerEvents: "none", children: [_jsx(S.DoorFrameGroundLineLeft, { children: _jsx(GroundDashLine, {}) }), _jsx(S.DoorFrameGroundLineRight, { children: _jsx(GroundDashLine, {}) }), _jsx(S.DoorFrameRect, {}), _jsx(S.OverlayCaptionBox, { children: _jsx(ShadowedCaption, { Caption: S.OverlayCaption, text: '문을 프레임 안에\n맞춰주세요' }) })] })), isEntrance && isGuideOverlayEnabled && photoFiles.length === 1 && (_jsxs(S.StairsOverlay, { pointerEvents: "none", children: [_jsx(ShadowedCaption, { Caption: S.StairsOverlayCaption, text: '계단/경사로의 높이를 확인할 수 있게\n약간 측면에서 촬영해주세요' }), _jsx(StairsOverlayIcon, { width: 230, height: 151, style: { marginLeft: S.StairsOverlayIconOffset } })] })), countdownDisplay !== null && (_jsx(S.CountdownOverlay, { pointerEvents: "none", children: _jsx(S.CountdownText, { children: countdownDisplay }) }))] })) : (_jsx(CameraNotAuthorized, {})), route.params.target !== 'elevator' &&
                        (!isEntrance || isGuideOverlayEnabled) && (_jsx(S.TipsAnchor, { children: _jsxs(S.Tips, { elementName: "camera_tips_button", onPress: () => {
                                const target = route.params.target;
                                if (isEntranceTarget(target)) {
                                    setIsEntranceGuideVisible(true);
                                }
                                else if (target === 'review' || target === 'toilet') {
                                    openGuide(target);
                                }
                            }, children: [_jsx(CircleInfoIcon, { width: 20, height: 20 }), _jsx(S.Tip, { children: isEntrance ? '예시 사진 >' : '사진 촬영 팁  >' })] }) })), isLoadingAlbum && (_jsxs(S.AlbumLoadingOverlay, { children: [_jsx(ActivityIndicator, { size: "large", color: "white" }), _jsx(S.AlbumLoadingText, { children: 'iCloud 등 클라우드에 저장된 사진은\n불러오는 데 시간이 걸릴 수 있습니다' })] }))] }), _jsxs(S.TakenPhotosSection, { children: [_jsx(S.TakenPhotos, { children: photoFiles.length > 0 ? (_jsx(GestureHandlerRootView, { children: _jsx(DraggableFlatList, { horizontal: true, contentContainerStyle: {
                                    justifyContent: 'center',
                                    overflow: 'visible',
                                    // 간격은 슬롯 자체의 marginHorizontal 로만 준다 (CameraScreen.style.ts
                                    // PHOTO_SLOT_GAP 참조). 여기에 gap 을 두면 footer 경계에서 이중 계산된다.
                                    paddingVertical: 6,
                                    flexGrow: 1,
                                }, data: photoFiles, onDragEnd: ({ data }) => setPhotoFiles(data), keyExtractor: (item) => item.uri, renderItem: ({ item, drag, isActive }) => (_jsxs(S.TakenPhotoItem, { elementName: "taken_photo_item", style: {
                                        opacity: isActive ? 0.5 : 1,
                                        transform: [{ scale: isActive ? 1.05 : 1 }],
                                    }, onLongPress: drag, onPress: () => openPreview(photoFiles.indexOf(item)), children: [_jsx(S.Thumbnail, { source: {
                                                uri: ImageFileUtils.filepathFromImageFile(item),
                                            } }), _jsx(S.CloseButton, { elementName: "taken_photo_delete_button", onPress: () => onPressX(item), children: _jsx(CircleCloseIcon, { width: 20, height: 20 }) })] }, item.uri)), ListFooterComponent: isEntrance ? (_jsx(S.PlaceholderRow, { children: ENTRANCE_PHOTO_SLOT_PLACEHOLDERS.slice(photoFiles.length).map((placeholder, i) => (_jsx(S.PlaceholderSlot, { children: placeholder && (_jsx(S.PlaceholderImage, { source: placeholder })) }, i))) })) : null }) })) : isEntrance ? (_jsx(S.PlaceholderRow, { children: ENTRANCE_PHOTO_SLOT_PLACEHOLDERS.map((placeholder, i) => (_jsx(S.PlaceholderSlot, { children: placeholder && _jsx(S.PlaceholderImage, { source: placeholder }) }, i))) })) : null }), !isEntrance && photoFiles.length === 0 && (_jsxs(S.PhotoCaption, { children: ["\uCD5C\uB300 ", photoLimit, "\uC7A5\uAE4C\uC9C0 \uCD2C\uC601\uD560 \uC218 \uC788\uC5B4\uC694"] }))] }), _jsxs(S.ActionsWrapper, { children: [visibleTooltip !== null && (_jsx(Tooltip, { text: visibleTooltip === 'albumLocked'
                            ? '1개의 장소를 현장 등록하면\n앨범 등록이 가능해요'
                            : visibleTooltip === 'albumActivated'
                                ? '이제, 앨범에 있는 사진을\n바로 등록할 수 있어요!'
                                : '사진 촬영이 어렵다면\n가이드의 도움을 받을 수 있어요!', 
                        // Figma: 앨범 툴팁은 bubble x=28 · 꼬리 8(중심 13) → 절대 41,
                        // 가이드 툴팁은 bubble x=12 · 꼬리 86(중심 91) → 절대 103.
                        // 각 버튼 원 중심(앨범 42.5 / 가이드 103.5)을 가리킨다.
                        bubbleLeft: visibleTooltip === 'guideIntro' ? 12 : 28, tailPosition: visibleTooltip === 'guideIntro' ? 91 : 13, 
                        // Figma(174:7786): 툴팁 y=611..661(높이 50), ActionsWrapper(촬영 버튼)
                        // top 은 655 → 툴팁 하단이 앵커 상단보다 6 아래로 걸친다.
                        offset: -6, style: { opacity: tooltipOpacity } })), _jsxs(S.SideButton, { left: 20, elementName: "camera_album_button", isDimmed: !isAlbumUploadAllowed, accessibilityRole: "button", accessibilityLabel: "\uC568\uBC94\uC5D0\uC11C \uC0AC\uC9C4 \uC120\uD0DD", accessibilityState: { disabled: !isAlbumUploadAllowed }, onPress: () => {
                            if (!isAlbumUploadAllowed) {
                                ToastUtils.show('1개의 장소를 현장 등록하면 앨범 등록이 가능해요');
                                return;
                            }
                            selectFromAlbum();
                        }, children: [_jsx(S.SideButtonCircle, { children: _jsx(AlbumIcon, { width: S.SIDE_BUTTON_ICON_SIZE, height: S.SIDE_BUTTON_ICON_SIZE, 
                                    // Figma(174:6959): 비활성 앨범 아이콘은 #A0A2AE
                                    color: isAlbumUploadAllowed ? 'white' : color.gray40v2 }) }), _jsx(S.SideButtonLabel, { children: "\uC568\uBC94" })] }), isEntrance && (_jsxs(S.SideButton, { left: 81, elementName: "camera_guide_button", accessibilityRole: "button", accessibilityLabel: "\uCD2C\uC601 \uAC00\uC774\uB4DC", accessibilityState: { selected: isGuideOverlayEnabled }, onPress: () => setIsGuideOverlayEnabled(on => !on), children: [_jsx(S.SideButtonCircle, { children: _jsx(GuideIcon, { width: S.SIDE_BUTTON_ICON_SIZE, height: S.SIDE_BUTTON_ICON_SIZE, color: isGuideOverlayEnabled ? color.yellow30 : 'white' }) }), _jsx(S.SideButtonLabel, { isOn: isGuideOverlayEnabled, children: "\uAC00\uC774\uB4DC" })] })), _jsx(S.CaptureButton, { elementName: "camera_capture_button", disabled: !canTakeMore || isTakingPhoto, onPress: handleCapturePress, children: _jsx(S.CaptureInnerDeco, {}) }), (device === null || device === void 0 ? void 0 : device.hasFlash) && (_jsxs(S.SideButton, { right: 81, elementName: "camera_flash_button", accessibilityRole: "button", accessibilityLabel: "\uD50C\uB798\uC2DC", accessibilityState: { selected: flash === 'on' }, onPress: toggleFlash, children: [_jsx(S.SideButtonCircle, { children: flash === 'on' ? (_jsx(FlashOnIcon, { width: S.SIDE_BUTTON_ICON_SIZE, height: S.SIDE_BUTTON_ICON_SIZE })) : (_jsx(FlashOffIcon, { width: S.SIDE_BUTTON_ICON_SIZE, height: S.SIDE_BUTTON_ICON_SIZE })) }), _jsx(S.SideButtonLabel, { children: "\uD50C\uB798\uC2DC" })] })), _jsxs(S.SideButton, { right: 20, elementName: "camera_timer_button", logParams: { timerSeconds }, accessibilityRole: "button", accessibilityLabel: "\uD0C0\uC774\uBA38", onPress: cycleTimer, children: [_jsx(S.SideButtonCircle, { children: _jsx(ClockIcon, { width: S.SIDE_BUTTON_ICON_SIZE, height: S.SIDE_BUTTON_ICON_SIZE, color: timerSeconds === 0 ? 'white' : color.yellow30 }) }), _jsx(S.SideButtonLabel, { isOn: timerSeconds !== 0, children: timerSeconds === 0 ? '타이머' : `${timerSeconds}초` })] })] }), isEntranceGuideVisible && (_jsx(EntrancePhotoGuideCarousel, { onDone: () => setIsEntranceGuideVisible(false) }))] }));
}
/**
 * Figma(174:6996 / 190:8385) 는 동일한 DROP_SHADOW 를 **3겹** 쌓아 halo 를 진하게 만든다.
 * RN Text 는 textShadow 를 1겹만 지원하고, radius 를 키우면 두꺼워지는 대신 퍼져서 옅어진다.
 * 그래서 같은 문구를 겹쳐 그려 **그림자만 누적**시킨다 — 아래 겹의 글자는 맨 위 겹에 완전히
 * 가려지므로 글자 자체는 한 겹으로 보인다.
 */
const CAPTION_SHADOW_LAYERS = 3;
function ShadowedCaption({ Caption, text, }) {
    return (_jsxs(S.CaptionStack, { children: [Array.from({ length: CAPTION_SHADOW_LAYERS - 1 }).map((_, i) => (_jsx(Caption, { style: StyleSheet.absoluteFillObject, children: text }, i))), _jsx(Caption, { children: text })] }));
}
// Figma(113:5048/5049): stroke white 2px, dash [4,4]. 컨테이너 폭 전체를 채운다.
function GroundDashLine() {
    return (_jsx(Svg, { width: "100%", height: 2, children: _jsx(Line, { x1: "0", y1: "1", x2: "100%", y2: "1", stroke: "white", strokeWidth: 2, strokeDasharray: "4,4" }) }));
}
function cropToRect(taken) {
    return __awaiter(this, void 0, void 0, function* () {
        const isLandscape = taken.orientation === 'landscape-left' ||
            taken.orientation === 'landscape-right';
        // 플랫폼별로 width/height swap 방향이 다름
        // Android: isLandscape일 때 taken.width가 실제 imageWidth
        // iOS: isLandscape일 때 taken.height가 실제 imageWidth
        const isIOS = Platform.OS === 'ios';
        const imageWidth = isLandscape
            ? isIOS
                ? taken.height
                : taken.width
            : isIOS
                ? taken.width
                : taken.height;
        const imageHeight = isLandscape
            ? isIOS
                ? taken.width
                : taken.height
            : isIOS
                ? taken.height
                : taken.width;
        const size = Math.min(imageWidth, imageHeight);
        const offset = {
            x: Math.floor(Math.max(0, (imageWidth - size) / 2)),
            y: Math.floor(Math.max(0, (imageHeight - size) / 2)),
        };
        const cropped = yield ImageEditor.cropImage(ImageFileUtils.filepath(taken.path), {
            offset: { x: offset.x, y: offset.y },
            size: { width: size, height: size },
            // iOS 26에서 HEIC 입력이 후속 단계에서 실패하는 케이스가 있어 JPEG로 강제.
            format: 'jpeg',
        });
        return { cropped, size };
    });
}
